import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  signal,
} from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import {
  AttestationTypeSummary,
  IntendedUse,
  credentialsOfType,
  indexByAttestationType,
  purposeText,
} from "../requested-data";
import {
  WatchContext,
  attestationTypeLink,
  intendedUseLink,
  matchesText,
  viewLink,
} from "../watch-view-model";
import { CertificateInspector } from "./certificate-dialog.component";

type Scope = "current" | "all";

const CLAIM_PREVIEW = 12;

/** Selects the intended uses that request no declared claims. */
export const UNDECLARED = "\u0000undeclared";

/** Result of comparing the derived intended uses with /intended-uses. */
export type IntendedUseCheck =
  | { status: "consistent" | "inconsistent"; asOfSequence: string }
  | { status: "unavailable"; reason: string };

interface WrpGroup {
  wrpId: string;
  name: string;
  uses: IntendedUse[];
}

@Component({
  selector: "watch-requests-view",
  imports: [MatIconModule],
  templateUrl: "./requests-view.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestsViewComponent {
  private readonly inspector = inject(CertificateInspector);

  readonly intendedUses = input.required<IntendedUse[]>();
  readonly context = input.required<WatchContext>();
  readonly check = input<IntendedUseCheck>();
  readonly selectedType = model<string | undefined>();

  readonly query = signal("");
  readonly scope = signal<Scope>("current");
  readonly claim = signal<string | undefined>(undefined);
  readonly allClaims = signal(false);
  readonly scopes: Array<{ value: Scope; label: string }> = [
    { value: "current", label: "Current" },
    { value: "all", label: "Incl. historic" },
  ];

  readonly summaries = computed(() =>
    indexByAttestationType(
      this.intendedUses().filter(
        (use) => this.scope() === "all" || use.current,
      ),
    ),
  );

  readonly counts = computed(() => ({
    current: this.intendedUses().filter((use) => use.current).length,
    all: this.intendedUses().length,
  }));

  /** Types whose name or any requested claim matches the search. */
  readonly visible = computed(() =>
    this.summaries()
      .map((summary) => ({
        summary,
        matchedClaims: this.query().trim()
          ? summary.claims.filter((claim) =>
              matchesText(this.query(), claim.path),
            )
          : [],
      }))
      .filter(
        ({ summary, matchedClaims }) =>
          matchedClaims.length > 0 ||
          matchesText(this.query(), summary.type || "no attestation type"),
      ),
  );

  readonly selected = computed<AttestationTypeSummary | undefined>(
    () =>
      this.summaries().find(
        (summary) => summary.type === this.selectedType(),
      ) ?? this.visible()[0]?.summary,
  );

  /** WRPs requesting the selected type, narrowed to the selected claim. */
  readonly groups = computed<WrpGroup[]>(() => {
    const summary = this.selected();
    if (!summary) return [];
    const claim = this.claim();
    const uses =
      claim === UNDECLARED
        ? summary.undeclared
        : claim
          ? (summary.claims.find((entry) => entry.path === claim)?.uses ?? [])
          : summary.uses;
    const groups = new Map<string, WrpGroup>();
    for (const use of uses) {
      const group = groups.get(use.wrpId) ?? {
        wrpId: use.wrpId,
        name: this.context().names.get(use.wrpId) ?? use.wrpId,
        uses: [],
      };
      group.uses.push(use);
      groups.set(use.wrpId, group);
    }
    return [...groups.values()].sort((left, right) =>
      left.name.localeCompare(right.name),
    );
  });

  /** The most requested claims, plus the selected one; all on request. */
  readonly shownClaims = computed(() => {
    const claims = this.selected()?.claims ?? [];
    if (this.allClaims() || claims.length <= CLAIM_PREVIEW) return claims;
    const top = claims.slice(0, CLAIM_PREVIEW);
    const selected = claims.find((entry) => entry.path === this.claim());
    return selected && !top.includes(selected) ? [...top, selected] : top;
  });

  readonly undeclared = UNDECLARED;
  readonly claimPreview = CLAIM_PREVIEW;
  private readonly selectedKey = computed(() => this.selected()?.type);

  constructor() {
    // A claim filter belongs to one type; another type starts unfiltered.
    effect(() => {
      this.selectedKey();
      this.claim.set(undefined);
      this.allClaims.set(false);
    });
  }

  select(summary: AttestationTypeSummary): void {
    this.selectedType.set(summary.type);
  }

  toggleClaim(path: string): void {
    this.claim.update((current) => (current === path ? undefined : path));
  }

  setScope(scope: Scope): void {
    this.scope.set(scope);
  }

  onQuery(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }

  typeName(type: string): string {
    return type || "No attestation type";
  }

  formatLabel(format: string): string {
    return (
      { "dc+sd-jwt": "SD-JWT VC", "vc+sd-jwt": "SD-JWT VC", mso_mdoc: "mdoc" }[
        format
      ] ?? format
    );
  }

  purpose(use: IntendedUse): string {
    return purposeText(use.purpose) ?? "No purpose given";
  }

  /** Claim paths the intended use requests for the selected type. */
  claimPaths(use: IntendedUse): string[] | undefined {
    const credentials = credentialsOfType(use, this.selected()?.type ?? "");
    if (credentials.every((credential) => !credential.claims)) {
      return undefined;
    }
    return [
      ...new Set(
        credentials.flatMap((credential) =>
          (credential.claims ?? []).map((claim) => claim.normalizedPath),
        ),
      ),
    ];
  }

  inspect(use: IntendedUse): void {
    this.inspector.open(use.latest, this.context());
  }

  apiLink(summary: AttestationTypeSummary): string {
    return attestationTypeLink(this.context(), summary.type, summary.formats);
  }

  intendedUseLink(use: IntendedUse): string {
    return intendedUseLink(this.context(), use.id);
  }

  wrpLink(wrpId: string): string {
    return viewLink("register", wrpId);
  }
}
